import React, { useState } from 'react'
import { Send, CheckCircle, MessageCircle, Users, HelpCircle, AlertCircle } from 'lucide-react'
import { Button } from '../ui/Button'
import { Input } from '../ui/Input'
import { Label } from '../ui/Label'
import { Textarea } from '../ui/Textarea'
import { ContactFormData, ContactSubject } from '../../types/support.types'
import { communicationService, CommunicationServiceError } from '../../services/communicationService'
import { cn } from '../../lib/utils'

interface ContactFormProps {
  subjects: ContactSubject[]
  className?: string
}

export const ContactForm: React.FC<ContactFormProps> = ({ subjects, className = '' }) => {
  const [formData, setFormData] = useState<ContactFormData>({
    name: '',
    email: '',
    subject: '',
    message: ''
  })
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isSubmitted, setIsSubmitted] = useState(false)
  const [errors, setErrors] = useState<Partial<ContactFormData>>({})
  const [submitError, setSubmitError] = useState<string | null>(null)

  const validateForm = (): boolean => {
    const newErrors: Partial<ContactFormData> = {}

    // Email validation
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required'
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address'
    }

    // Subject validation
    if (!formData.subject) {
      newErrors.subject = 'Please select a subject'
    }

    // Message validation
    if (!formData.message.trim()) {
      newErrors.message = 'Please enter your message'
    } else if (formData.message.trim().length < 10) {
      newErrors.message = 'Message must be at least 10 characters long'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    if (!validateForm()) return

    setIsSubmitting(true)
    setSubmitError(null)
    
    try {
      // Send both support notification and user confirmation emails
      await communicationService.sendContactFormSubmission(formData)

      // Log success for debugging
      console.log('Contact form submitted successfully:', {
        ...formData,
        submittedAt: new Date().toISOString()
      })

      setIsSubmitted(true)
      setFormData({ name: '', email: '', subject: '', message: '' })
      setErrors({})
      
    } catch (error) {
      console.error('Error submitting contact form:', error)
      
      if (error instanceof CommunicationServiceError) {
        setSubmitError(
          error.code === 'RATE_LIMIT_EXCEEDED' 
            ? 'Too many requests. Please try again in a few minutes.'
            : error.message
        )
      } else {
        setSubmitError('Failed to send your message. Please try again or contact us directly at support@stratiqx.ai.')
      }
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleInputChange = (field: keyof ContactFormData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }))
    
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: undefined }))
    }
  }

  const resetForm = () => {
    setIsSubmitted(false)
    setFormData({ name: '', email: '', subject: '', message: '' })
    setErrors({})
    setSubmitError(null)
  }

  if (isSubmitted) {
    return (
      <div className={`${className}`} id="contact-form">
        <div className="text-center">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <CheckCircle className="w-8 h-8 text-green-600" />
          </div>
          <h3 className="text-xl font-semibold text-gray-900 mb-2">
            Message Sent Successfully!
          </h3>
          <p className="text-gray-600 mb-4">
            Thank you for contacting us. We'll respond to your inquiry within 24-48 hours.
          </p>
          <p className="text-sm text-gray-500 mb-6">
            Check your email for confirmation details.
          </p>
          <Button 
            variant="outline" 
            onClick={resetForm}
            className="mx-auto"
          >
            Send Another Message
          </Button>
        </div>
      </div>
    )
  }

  return (
    <div className={`${className}`} id="contact-form">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Still Need Help?</h2>
        <p className="text-gray-600">
          Can't find what you're looking for? Send us a message and we'll get back to you within 24-48 hours.
        </p>
      </div>

      {/* Info Banner */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
        <div className="flex items-start gap-3">
          <HelpCircle className="h-5 w-5 text-blue-600 mt-0.5 flex-shrink-0" />
          <div>
            <h4 className="font-semibold text-blue-900 mb-1">
              Get Personalized Support
            </h4>
            <p className="text-sm text-blue-800">
              Our support team will provide detailed assistance with your StratIQX onboarding, 
              configuration questions, or technical issues.
            </p>
          </div>
        </div>
      </div>
      
      <div className="space-y-6">
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Contact Information */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              <Users className="h-5 w-5 text-blue-600" />
              Contact Information
            </h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="name">Name (Optional)</Label>
                <Input
                  id="name"
                  type="text"
                  placeholder="Your full name"
                  value={formData.name}
                  onChange={(e) => handleInputChange('name', e.target.value)}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="email">Email Address *</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="your.email@company.com"
                  value={formData.email}
                  onChange={(e) => handleInputChange('email', e.target.value)}
                  className={errors.email ? 'border-red-300' : ''}
                />
                {errors.email && (
                  <p className="text-sm text-red-600">{errors.email}</p>
                )}
              </div>
            </div>
          </div>

          {/* Support Request */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              <MessageCircle className="h-5 w-5 text-blue-600" />
              Support Request
            </h3>
            
            <div className="space-y-2">
              <Label htmlFor="subject">Subject *</Label>
              <select
                id="subject"
                value={formData.subject}
                onChange={(e) => handleInputChange('subject', e.target.value)}
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
              >
                <option value="">Select a topic...</option>
                {subjects.map(subject => (
                  <option key={subject.id} value={subject.value}>
                    {subject.label}
                  </option>
                ))}
              </select>
              {errors.subject && (
                <p className="text-sm text-red-600">{errors.subject}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="message">Message *</Label>
              <Textarea
                id="message"
                placeholder="Please describe your question or issue in detail. Include any specific steps you've tried or error messages you've encountered..."
                value={formData.message}
                onChange={(e) => handleInputChange('message', e.target.value)}
                className={cn("min-h-[120px] resize-none", errors.message ? 'border-red-300' : '')}
              />
              {errors.message && (
                <p className="text-sm text-red-600">{errors.message}</p>
              )}
              <p className="text-xs text-gray-500">
                Minimum 10 characters ({formData.message.length}/10)
              </p>
            </div>
          </div>
        </form>
      </div>

      {/* Error Display */}
      {submitError && (
        <div className="mx-8 mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
          <div className="flex items-start gap-3">
            <AlertCircle className="h-5 w-5 text-red-600 mt-0.5 flex-shrink-0" />
            <div>
              <h4 className="font-semibold text-red-900 mb-1">
                Unable to Send Message
              </h4>
              <p className="text-sm text-red-800">{submitError}</p>
            </div>
          </div>
        </div>
      )}

      {/* Form Footer */}
      <div className="border-t border-gray-200 pt-6 mt-6 bg-gray-50 -mx-8 px-8 pb-8 rounded-b-lg">
        <div className="flex items-center justify-between">
          <p className="text-sm text-gray-600">
            * Required fields
          </p>
          <div className="flex gap-3">
            <Button
              onClick={handleSubmit}
              disabled={isSubmitting}
              className="flex items-center gap-2"
            >
              {isSubmitting ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Sending...
                </>
              ) : (
                <>
                  <Send className="w-4 h-4" />
                  Send Message
                </>
              )}
            </Button>
          </div>
        </div>
        
        {/* Privacy Note */}
        <p className="text-xs text-gray-500 text-center mt-4">
          Your information will only be used to respond to your inquiry. We respect your privacy and won't share your details with third parties.
        </p>
      </div>
    </div>
  )
}